// What a room tool does under the pointer, decided once (docs/claude/rooms.md,
// *One target per room tool*): its hover paints the target and its press applies
// it, so the green or red the cursor shows is what the click does. Every target
// is looked for in the same rooms, `editableRooms`: a sauna or a steam room the
// one pickability rule lets through (not frozen, not on a hidden or locked layer
// — main.ts passes `instLocked`), drawn on top first. DOM-free and unit-tested.
import type {
  HeaterWall,
  InnerWall,
  ProductInstance,
  SaunaDoor,
  Vec2,
  WallSide,
} from '../shared/types';
import { MAX_BENCH_TIERS } from './benches';
import { wallThicknessOf } from './dent';
import {
  defaultDoor,
  defaultDoorWidth,
  doorKindRule,
  roomDoors,
} from './door';
import {
  pointInPolygon,
  toInstanceLocal,
} from './geometry';
import { wallHit } from './glass';
import {
  benchFreeSpans,
  benchOccupies,
  benchSpanAt,
  benchTierPreview,
  doorRun,
  heaterWallBands,
  heaterWallOn,
  innerBenchPaintOrder,
  isPolyRoom,
  partitionBenchJointAt,
  polyBenchJointAt,
  polyBenchPick,
  polyEdges,
  polyInnerBenchBands,
  polyInnerWallHit,
  polyInnerWallMetrics,
  polyWallHit,
  segToWall,
  wallRuns,
  wallView,
} from './polyRoom';
import {
  doorFootprint,
  roomDoor,
} from './roomDoor';

type PolyBench = NonNullable<ProductInstance['polyBenches']>[number];

/** The rooms a room tool may edit, drawn on top first. */
export function editableRooms(instances: readonly ProductInstance[], editable: (room: ProductInstance) => boolean): ProductInstance[] {
  const out: ProductInstance[] = [];
  for (let i = instances.length - 1; i >= 0; i--) {
    const room = instances[i];
    if (room.autoBenches && editable(room)) out.push(room);
  }
  return out;
}

/** The top polygonal room whose outline holds the point. */
export function roomAt(rooms: readonly ProductInstance[], world: Vec2): ProductInstance | null {
  return rooms.find((r) => isPolyRoom(r) && pointInPolygon(toInstanceLocal(r, world), r.polygon!)) ?? null;
}

// ---- Partitions and walls ---------------------------------------------------

/** The top interior partition under the point. */
export function partitionAt(rooms: readonly ProductInstance[], world: Vec2): { room: ProductInstance; index: number; wall: InnerWall; local: Vec2 } | null {
  for (const room of rooms) {
    if (!isPolyRoom(room)) continue;
    const local = toInstanceLocal(room, world);
    const index = polyInnerWallHit(room, local);
    const wall = index >= 0 ? room.innerWalls?.[index] : undefined;
    if (wall) return { room, index, wall, local };
  }
  return null;
}

/** A room's wall picked: a polygonal room's segment (`edge`, `pos` along it), a
 *  legacy rectangular room's side (`wall`). */
export interface WallPick { room: ProductInstance; local: Vec2; wall: WallSide; pos: number; edge?: number; dist: number }

/** The room wall NEAREST the point within `reach` (mm), across every room — not
 *  merely the top room whose wall is in reach — so a pane drawn on one room's
 *  wall never lands on a room a few centimetres away. */
export function nearestWallAt(rooms: readonly ProductInstance[], world: Vec2, reach: number): WallPick | null {
  let best: WallPick | null = null;
  for (const room of rooms) {
    const local = toInstanceLocal(room, world);
    if (isPolyRoom(room)) {
      const ph = polyWallHit(room, local, reach);
      if (ph && ph.dist < (best?.dist ?? Infinity)) best = { room, local, wall: 'front', edge: ph.edge, pos: ph.pos, dist: ph.dist };
      continue;
    }
    const hit = wallHit(room, local, reach);
    if (hit && hit.dist < (best?.dist ?? Infinity)) best = { room, local, wall: hit.wall, pos: hit.pos, dist: hit.dist };
  }
  return best;
}

/** A polygonal room's segment pick re-expressed by WALL (corner to corner): a
 *  pane or an opening is dragged along the whole wall, across any jambs on it. */
export function byWall(pick: WallPick): WallPick {
  if (pick.edge == null) return pick;
  const at = segToWall(pick.room, pick.edge);
  return at ? { ...pick, edge: at.wall, pos: at.offset + pick.pos } : pick;
}

// ---- The Glass, Opening and Door tools ----------------------------------------

/** Where a press of the Glass tool (`overPartitions`) or the Opening tool starts
 *  its pane: the partition under the point (the Glass tool only), its distance
 *  along the partition's inward length; else the nearest wall, by wall. */
export type PaneTarget =
  | { room: ProductInstance; local: Vec2; partition: InnerWall; along: number }
  | (WallPick & { partition?: undefined });

export function paneTargetAt(rooms: readonly ProductInstance[], world: Vec2, reach: number, overPartitions: boolean): PaneTarget | null {
  if (overPartitions) {
    const p = partitionAt(rooms, world);
    const m = p?.wall.id ? polyInnerWallMetrics(p.room, p.wall.edge ?? -1, p.local) : null;
    if (p && m) return { room: p.room, local: p.local, partition: p.wall, along: Math.max(0, Math.min(Math.max(0, p.wall.length), m.depth)) };
  }
  const w = nearestWallAt(rooms, world, reach);
  return w && byWall(w);
}

/** Where the Door tool puts a door: on the partition under the point, else on
 *  the nearest wall — never on a wall that carries a hidden heater wall (the
 *  heater-wall tool refuses a door's wall: the same rule from the other side). */
export type DoorTarget =
  | { room: ProductInstance; local: Vec2; partition: InnerWall }
  | { room: ProductInstance; partition?: undefined; edge: number; pos: number };

export function doorTargetAt(rooms: readonly ProductInstance[], world: Vec2, reach: number): DoorTarget | null {
  const p = partitionAt(rooms, world);
  if (p) return { room: p.room, local: p.local, partition: p.wall };
  const w = nearestWallAt(rooms, world, reach);
  if (!w) return null;
  if (heaterWallOn(w.room, segToWall(w.room, w.edge ?? 0)?.wall ?? -1)) return null;
  return { room: w.room, edge: w.edge ?? 0, pos: w.pos };
}

/** The door the Door tool puts at a point, not yet in its room: the room, the
 *  door — the default one, as wide as 60 % of a short wall at most, centred
 *  where clicked and kept on its wall —, the partition it goes in (if any) and
 *  the room as its run reads it (a partition with no id yet given a stand-in). */
export interface DoorToAdd { room: ProductInstance; door: SaunaDoor; partition?: InnerWall; host: ProductInstance }

/** The id a partition with none stands in under, until the door is added. */
const NEW_PARTITION = '__new-partition';

export function doorToAdd(rooms: readonly ProductInstance[], world: Vec2, reach: number): DoorToAdd | null {
  const t = doorTargetAt(rooms, world, reach);
  if (!t) return null;
  const room = t.room;
  if (t.partition) {
    const iw = t.partition, id = iw.id ?? NEW_PARTITION;
    const host = iw.id ? room : { ...room, innerWalls: (room.innerWalls ?? []).map((w) => (w === iw ? { ...w, id } : w)) };
    const door = defaultDoor(Math.max(0, iw.length), { innerWallId: id });
    const run = doorRun(host, door);
    if (run && run.len > 0) {
      const along = (t.local.x - run.base.x) * run.along.x + (t.local.y - run.base.y) * run.along.y;
      door.offset = Math.max(door.width / 2, Math.min(run.len - door.width / 2, along)) - run.len / 2;
    }
    return { room, door, partition: iw, host };
  }
  const door = defaultDoor(room.width, { edge: t.edge }, room.category);
  const run = doorRun(room, door);
  if (run && run.len > 0) {
    door.width = Math.min(door.width, defaultDoorWidth(run.len, doorKindRule(room.category, false, false) === 'pt9'));
    door.offset = Math.max(door.width / 2, Math.min(run.len - door.width / 2, t.pos)) - run.len / 2;
  }
  return { room, door, host: room };
}

/** What the Door tool's hover shows of the door it would add (room local): its
 *  opening and the quarter its leaf sweeps, as the plan draws a door. */
export const doorToAddPolys = (d: DoorToAdd): Vec2[][] => {
  const rd = roomDoor(d.host, d.door);
  const poly = rd ? doorFootprint(rd) : [];
  return poly.length >= 3 ? [poly] : [];
};

// ---- The Inner-wall tool --------------------------------------------------------

/** What the Inner-wall tool's press does in the room under it: take hold of the
 *  partition under the point (a click without a drag removes it — so the hover
 *  shows it red), or start a new one off the wall in reach, a wall's thickness in
 *  from its corners. */
export type InnerWallTarget = { move: number } | { edge: number; pos: number };

export function innerWallTargetIn(room: ProductInstance, local: Vec2, reach: number): InnerWallTarget | null {
  const move = polyInnerWallHit(room, local);
  if (move >= 0) return { move };
  const view = wallView(room);
  const hit = polyWallHit(view, local, reach);
  if (!hit) return null;
  const len = polyEdges(view)[hit.edge].len, wt = wallThicknessOf(room);
  return { edge: hit.edge, pos: Math.max(wt, Math.min(len - wt, hit.pos)) };
}

// ---- The Heater-wall tool -------------------------------------------------------

/** Whether a room wall can take a hidden heater wall: a real wall, without one
 *  already, and without a door (a wall never carries both). */
export function canAddHeaterWall(room: ProductInstance, edge: number): boolean {
  if (!isPolyRoom(room) || edge < 0 || edge >= wallRuns(room).length) return false;
  if (heaterWallOn(room, edge)) return false;
  return !roomDoors(wallView(room)).some((d) => (d.edge ?? 0) === edge);
}

/** The top room's hidden heater wall under the point. */
export function heaterWallAt(rooms: readonly ProductInstance[], world: Vec2): { room: ProductInstance; wall: HeaterWall } | null {
  for (const room of rooms) {
    if (!isPolyRoom(room)) continue;
    const local = toInstanceLocal(room, world);
    const band = heaterWallBands(room).find((b) => b.poly.length >= 3 && pointInPolygon(local, b.poly));
    if (band) return { room, wall: band.wall };
  }
  return null;
}

/** What the Heater-wall tool does under the point: remove the heater wall there
 *  (red), else add one on the wall in reach of the room under it (green). */
export type HeaterWallTarget = { room: ProductInstance; remove: HeaterWall } | { room: ProductInstance; add: number };

export function heaterWallTargetAt(rooms: readonly ProductInstance[], world: Vec2, reach: number): HeaterWallTarget | null {
  const on = heaterWallAt(rooms, world);
  if (on) return { room: on.room, remove: on.wall };
  const room = roomAt(rooms, world);
  const hit = room ? polyWallHit(wallView(room), toInstanceLocal(room, world), reach) : null;
  return room && hit && canAddHeaterWall(room, hit.edge) ? { room, add: hit.edge } : null;
}

// ---- The Bench tool -------------------------------------------------------------

/** A partition's bench, as `polyInnerBenchBands` draws it. */
export type PartitionBench = ReturnType<typeof polyInnerBenchBands>[number];

/** The partition bench under `local`: the one painted on top. */
export function partitionBenchAt(room: ProductInstance, local: Vec2): PartitionBench | null {
  return innerBenchPaintOrder(polyInnerBenchBands(room)).reverse().find((b) => b.id && b.poly.length >= 3 && pointInPolygon(local, b.poly)) ?? null;
}

/** The face of partition `iwIndex` toward `local`. */
export function partitionFaceAt(room: ProductInstance, iwIndex: number, local: Vec2): 'a' | 'b' {
  const iw = room.innerWalls![iwIndex];
  const m = polyInnerWallMetrics(room, iw.edge ?? -1, local);
  return m && m.along < iw.pos ? 'a' : 'b';
}

/** How a new wall bench starts on `edge` (its depth and, in a steam room, its
 *  profile): main.ts's `newPolyBench`. */
export type NewTier = (room: ProductInstance, edge: number) => Omit<PolyBench, 'id' | 'edge'>;

/** The footprint the next tier on `edge` takes (a `span` clips it to a stretch
 *  between partitions): the real band builder on a clone, so it is the pieces the
 *  click makes. */
export function nextTierPolys(room: ProductInstance, edge: number, newTier: NewTier, span?: [number, number] | null): Vec2[][] {
  return benchTierPreview(room, edge, newTier(room, edge), span);
}

/** The edge whose next tier lands where `local` is — the empty strip just inward
 *  of its innermost bench — or null (none, or that stack is full). The tier cap
 *  is per stack: each side of a partition takes MAX_BENCH_TIERS of its own. */
export function nextTierEdgeAt(room: ProductInstance, local: Vec2, newTier: NewTier): number | null {
  const seen = new Set<number>();
  for (const b of room.polyBenches ?? []) {
    if (seen.has(b.edge)) continue;
    seen.add(b.edge);
    const edgeLen = polyEdges(wallView(room))[b.edge]?.len ?? 0;
    if (!edgeLen) continue;
    for (const sp of benchFreeSpans(room, b.edge)) {
      const tiersInSpan = (room.polyBenches ?? []).filter((x) => x.edge === b.edge &&
        (x.start ?? 0) < sp[1] - 1 && sp[0] < (x.end ?? edgeLen) - 1).length;
      if (tiersInSpan >= MAX_BENCH_TIERS) continue;
      const full = sp[0] <= 1 && sp[1] >= edgeLen - 1;
      if (nextTierPolys(room, b.edge, newTier, full ? null : sp).some((poly) => pointInPolygon(local, poly))) return b.edge;
    }
  }
  return null;
}

/** The stretch between partitions a next tier on `edge` fills under `local`, or
 *  null when it is the whole edge — so a stacked tier stays on the clicked side
 *  of a partition (stored benches are drawn as they are, never reconciled). */
export function nextTierSpanAt(room: ProductInstance, edge: number, local: Vec2): [number, number] | null {
  const e = polyEdges(wallView(room))[edge];
  if (!e) return null;
  const along = Math.max(0, Math.min(e.len, (local.x - e.a.x) * e.dir.x + (local.y - e.a.y) * e.dir.y));
  const span = benchSpanAt(room, edge, along);
  if (!span || (span[0] <= 1 && span[1] >= e.len - 1)) return null;
  return span;
}

/** What the Bench tool's press does in the room under it, in the order a press
 *  tries them: swap which bench wins a corner (`joint`) or a partition's meeting
 *  (`partitionJoint`); remove the partition bench under it (painted over the
 *  rest); add a tier on the partition face toward it; remove the wall bench under
 *  it; stack the next tier in the empty strip in front of a bench; else a first
 *  bench on the free stretch of the wall in reach. Both removals are one
 *  `removeBench` (its id and the outline the hover paints red): the bench's
 *  adapter takes it from whatever hosts it. */
export type BenchTarget =
  | { kind: 'firstBench'; edge: number; span: [number, number] | null }
  | { kind: 'joint'; corner: number; tier: number }
  | { kind: 'nextTier'; edge: number; span: [number, number] | null }
  | { kind: 'partitionBench'; iwIndex: number; face: 'a' | 'b' }
  | { kind: 'partitionJoint'; iwIndex: number; face: 'a' | 'b'; tier: number }
  | { kind: 'removeBench'; id: string; poly: Vec2[] };

export function benchTargetIn(room: ProductInstance, local: Vec2, reach: number, newTier: NewTier): BenchTarget | null {
  const jt = polyBenchJointAt(room, local);
  if (jt) return { kind: 'joint', corner: jt.corner, tier: jt.tier };
  const pj = partitionBenchJointAt(room, local);
  if (pj) return { kind: 'partitionJoint', ...pj };
  const ib = partitionBenchAt(room, local);
  if (ib) return { kind: 'removeBench', id: ib.id, poly: ib.poly };
  const iw = polyInnerWallHit(room, local);
  if (iw >= 0) return { kind: 'partitionBench', iwIndex: iw, face: partitionFaceAt(room, iw, local) };
  const pick = polyBenchPick(room, local);
  if (pick) return { kind: 'removeBench', id: pick.band.id, poly: pick.band.poly };
  const nt = nextTierEdgeAt(room, local, newTier);
  if (nt != null) return { kind: 'nextTier', edge: nt, span: nextTierSpanAt(room, nt, local) };
  const view = wallView(room);
  const ph = polyWallHit(view, local, reach);
  const span = ph ? benchSpanAt(room, ph.edge, ph.pos) : null;
  if (!ph || !span || benchOccupies(room, ph.edge, span)) return null;
  const edgeLen = polyEdges(view)[ph.edge]?.len ?? 0;
  return { kind: 'firstBench', edge: ph.edge, span: span[0] <= 1 && span[1] >= edgeLen - 1 ? null : span };
}
